import type { Canvas, CanvasEvents } from "fabric";
import { useEffect, useState } from "react";
import { useAppQuery } from "../../../../../http/useAppQuery.ts";
import type { DetailedImage } from "../../../types.ts";
import type { ImageLayer } from "./types.ts";
import { buildImageLayer } from "./utils/image.ts";

export const ImageLayerBuilder = ({
  layer,
  fabricCanvas,
}: {
  layer: ImageLayer;
  fabricCanvas?: Canvas;
}) => {
  const [contentUuid, setContentUuid] = useState(layer.contentUuid);

  useEffect(() => {
    if (!layer.canvas) return;

    const handleContentUuidChange = (
      event: CanvasEvents["layer:image:contentUuid:changed"],
    ) => {
      if (event.layer === layer) setContentUuid(event.newContentUuid);
    };

    layer.canvas.on("layer:image:contentUuid:changed", handleContentUuidChange);

    return () => {
      layer.canvas?.off(
        "layer:image:contentUuid:changed",
        handleContentUuidChange,
      );
    };
  }, [layer]);

  // FIXME: We need a way to make sure that the content is an image
  // not a video. We could either create a new route images/uuid
  // or add a query param to the current route.
  const { data: imageData } = useAppQuery<DetailedImage>({
    queryKey: `contents/${contentUuid}`,
    queryParamsKey: {
      size: "LARGE512",
    },
  });

  useEffect(() => {
    if (imageData?.width && imageData.height && imageData.url) {
      layer.set({
        targetWidth: imageData.width,
        targetHeight: imageData.height,
      });
      void buildImageLayer({
        imageLayer: layer,
        targetWidth: imageData.width,
        targetHeight: imageData.height,
        imageUrl: imageData.url,
      });
    }
  }, [imageData, layer, fabricCanvas]);

  // XXX: We don't need to render anything here, we just need to build the image layer that will be rendered by the fabric canvas.
  // As we cannot define hooks conditionally, we are forced to define per image layer (hence contentUuid) a hook that will get the image data and build the image layer.
  // FIXME: The day our backend is able to query several contents at once we will be able to build the different image layers in a single query and a use effect.
  // This would remove the need to define a hook per image layer and having empty components.
  return null;
};
